import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Heading,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { date as commonDate } from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';
import {
  numberToDollarFormattedRounded,
  numberToPercentageLabel,
} from 'packages/common-frontend/utils';
import { ScaleType } from 'recharts/types/util/types';

type DataType =
  | 'percentage'
  | 'currency'
  | 'years'
  | 'months'
  | 'days'
  | 'number'
  | 'epoch';

/// Line chart formatter utils ///

const tooltipFormatterPercentage = (value: number, name: string) => {
  const formattedValue = numberToPercentageLabel(value) || `${value}`;
  return [formattedValue, name];
};

const tooltipFormatterCurrency = (value: number, name: string) => {
  const formattedValue = numberToDollarFormattedRounded(value) || `${value}`;
  return [formattedValue, name];
};

const tooltipFormatterYears = (value: number, name: string) => {
  const formattedValue = `${value} years`;
  return [formattedValue, name];
};

const tooltipFormatterMonths = (value: number, name: string) => {
  const formattedValue = `${value} months`;
  return [formattedValue, name];
};

const tooltipFormatterDays = (value: number, name: string) => {
  const formattedValue = `${value} days`;
  return [formattedValue, name];
};

const tooltipFormatterNumber = (value: number, name: string) => {
  const formattedValue = `${value}`;
  return [formattedValue, name];
};

const tooltipFormatterEpoch = (value: number, name: string) => {
  const formattedValue = `${commonDate.epochToLocaleDateString(
    value,
    undefined,
    DateTime.DATE_FULL,
  )}`;
  return [formattedValue, name];
};

const tooltipFormatter: {
  [key in DataType]: (value: number, name: string) => string[];
} = {
  percentage: tooltipFormatterPercentage,
  currency: tooltipFormatterCurrency,
  years: tooltipFormatterYears,
  months: tooltipFormatterMonths,
  days: tooltipFormatterDays,
  number: tooltipFormatterNumber,
  epoch: tooltipFormatterEpoch,
};

const tooltipLabelFormatterDate = (value: string) => {
  return DateTime.fromISO(value).toFormat('LLLL dd, yyyy');
};

const tooltipLabelFormatterEpoch = (value: number) => {
  return commonDate.epochToLocaleDateString(
    value,
    undefined,
    DateTime.DATE_FULL,
  );
};

const tooltipLabelFormatter: {
  [key in 'date' | 'epoch']: (value: any) => string;
} = {
  date: tooltipLabelFormatterDate,
  epoch: tooltipLabelFormatterEpoch,
};

const axisTickFormatterPercentage = (value: number) => {
  return numberToPercentageLabel(value) || value.toString();
};

const axisTickFormatterCurrency = (value: number) => {
  return numberToDollarFormattedRounded(value) || value.toString();
};

const axisTickFormatterNumber = (value: number) => {
  return value.toString();
};

const axisTickFormatterEpoch = (value: number) => {
  return commonDate.epochToSlashedDateStr(value);
};

export const axisTickFormatter: {
  [key in DataType]: (value: any) => string;
} = {
  percentage: axisTickFormatterPercentage,
  currency: axisTickFormatterCurrency,
  years: axisTickFormatterNumber,
  months: axisTickFormatterNumber,
  days: axisTickFormatterNumber,
  number: axisTickFormatterNumber,
  epoch: axisTickFormatterEpoch,
};

const legendFormatter = (value: string) => {
  return _.startCase(value);
};

/// Reference line label ///

const formatReferenceLineLabel = (value: number, format: DataType) => {
  switch (format) {
    case 'percentage':
      return axisTickFormatter.percentage(value);
    case 'currency':
      return axisTickFormatter.currency(value);
    default:
      return axisTickFormatter.number(value);
  }
};

/// Custom tick generators ///

const getMonthTicksByEpochs = (
  chartData: { xAxisValue: number; [key: string]: any }[],
) => {
  const sortedChartData = _.sortBy(chartData, 'xAxisValue');
  const startMonth = DateTime.fromMillis(sortedChartData[0].xAxisValue).startOf(
    'month',
  );
  const endMonth = DateTime.fromMillis(
    sortedChartData[chartData.length - 1].xAxisValue,
  ).startOf('month');
  const dateDiff = endMonth.diff(startMonth, ['months', 'days']);
  const monthsDiff = dateDiff.toObject().months;

  // if all chart data are in the same month, return only one tick for that month
  if (!monthsDiff) {
    return [startMonth.toMillis()];
  }

  // return an array of the first days of consecutive months, from the start month to the end month
  return _.map(_.range(monthsDiff + 1), (i) => {
    return startMonth.plus({ months: i }).toMillis();
  });
};

const xAxisCustomMonthTicks = {
  epoch: getMonthTicksByEpochs,
};

/// Line component ///

const generateLineChartLineComponents = ({
  data,
  coloredLines,
  hideNodes,
  customChartNodeComponent,
}: {
  data: {
    xAxisValue: any;
    [key: string]: any;
  }[];
  coloredLines?: boolean;
  hideNodes?: boolean;
  customChartNodeComponent?: React.ReactElement;
}) => {
  // finley theme colors & shades for line colors
  const {
    cobalt,
    forest,
    lime,
    gold,
    vermillion,
    rose,
    violet,
    indigo,
  } = colors;
  const shades = ['900', '400'] as const;
  const lineColors = _.flatMap(shades, (shade) => {
    return [
      cobalt[shade],
      forest[shade],
      lime[shade],
      gold[shade],
      vermillion[shade],
      rose[shade],
      violet[shade],
      indigo[shade],
    ];
  });
  let colorIndex: number = -1;

  const dot = hideNodes ? false : customChartNodeComponent;

  const dataKeys = _.uniq(
    _.flatMap(data, (datapoint) => {
      return Object.keys(datapoint);
    }),
  );

  // generate a line for each unique dataKey from the chart data, rotating through the line colors
  return _.compact(
    _.map(dataKeys, (dataKey) => {
      if (dataKey !== 'xAxisValue') {
        colorIndex += 1;
        if (colorIndex >= lineColors.length - 1) {
          colorIndex = -1;
        }
        return (
          <Line
            type="monotone"
            connectNulls
            dataKey={dataKey}
            stroke={coloredLines ? lineColors[colorIndex] : 'grey'}
            strokeWidth={2}
            dot={dot}
            key={dataKey}
            activeDot={hideNodes ? { r: 6 } : undefined}
          />
        );
      }
      return undefined;
    }),
  );
};

/// Generic line chart ///

type LineChartWithFormattersProps = {
  data: {
    xAxisValue: any;
    [key: string]: any;
  }[];
  title?: string;
  subtitle?: string;
  xAxisScaleType?: ScaleType;
  xAxisLabel?: string;
  xAxisCustomTicks?: keyof typeof xAxisCustomMonthTicks;
  xAxisTickFormatterType?: keyof typeof axisTickFormatter;
  xAxisTickFormatter?: (value: any) => string;
  yAxisLabel?: string;
  yAxisTickFormatterType?: keyof typeof axisTickFormatter;
  referenceLine?: { value: number; format: DataType };
  tooltip?: {
    tooltipType: keyof typeof tooltipFormatter;
    tooltipLabelType: keyof typeof tooltipLabelFormatter;
    tooltipName?: string;
  };
  legend?: boolean;
  coloredLines?: boolean;
  hideNodes?: boolean;
  customChartNodeComponent?: React.ReactElement;
};

export const LineChartWithFormatters = ({
  title,
  subtitle,
  data,
  xAxisCustomTicks,
  xAxisScaleType,
  xAxisLabel,
  xAxisTickFormatterType,
  xAxisTickFormatter,
  yAxisLabel,
  yAxisTickFormatterType,
  referenceLine,
  tooltip,
  legend,
  coloredLines,
  hideNodes,
  customChartNodeComponent,
}: LineChartWithFormattersProps) => {
  return (
    <Box height="100%">
      {title && (
        <Heading as="h3" fontSize="xl" mb="30px">
          {title}
        </Heading>
      )}
      {subtitle && (
        <Alert status="warning" display="flex" w="fit-content" mb="30px">
          <AlertIcon />
          <AlertDescription>
            <Text>{subtitle}</Text>
          </AlertDescription>
        </Alert>
      )}
      <ResponsiveContainer height={400} width="100%" debounce={100}>
        <LineChart
          margin={{
            top: 10,
            right: 10,
            left: 10,
            bottom: 0,
          }}
          data={data}
        >
          <CartesianGrid vertical={false} />
          {tooltip && (
            <Tooltip
              formatter={(value: number, name: string) =>
                tooltipFormatter[tooltip.tooltipType](
                  value,
                  tooltip.tooltipName ? tooltip.tooltipName : _.startCase(name),
                )
              }
              labelFormatter={tooltipLabelFormatter[tooltip.tooltipLabelType]}
              labelStyle={{ fontWeight: 'bold' }}
              itemStyle={{ color: colors.gray[500] }}
              cursor={{ stroke: 'none' }}
              contentStyle={{ fontSize: '12px' }}
            />
          )}
          {legend && (
            <Legend
              layout="vertical"
              verticalAlign="top"
              align="right"
              formatter={legendFormatter}
            />
          )}
          <XAxis
            xAxisId={0}
            dataKey="xAxisValue"
            label={{
              value: xAxisLabel,
              position: 'insideBottom',
              style: { textAnchor: 'middle', fontWeight: 'bold' },
            }}
            axisLine={false}
            tickLine
            tick={{ fontWeight: 600 }}
            ticks={
              xAxisCustomTicks
                ? xAxisCustomMonthTicks[xAxisCustomTicks](data)
                : undefined
            }
            tickFormatter={
              xAxisTickFormatter ??
              (xAxisTickFormatterType
                ? axisTickFormatter[xAxisTickFormatterType]
                : undefined)
            }
            height={70}
            padding={{ left: 40, right: 40 }}
            domain={['auto', 'auto']}
            scale={xAxisScaleType}
            type={xAxisScaleType === 'time' ? 'number' : 'category'}
          />
          <YAxis
            label={
              yAxisLabel
                ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fontWeight: 'bold' },
                  }
                : undefined
            }
            width={80}
            tickFormatter={
              yAxisTickFormatterType
                ? axisTickFormatter[yAxisTickFormatterType]
                : undefined
            }
            tick={{ fontWeight: 600 }}
            tickLine={false}
            axisLine={false}
          />
          {referenceLine && (
            <ReferenceLine
              stroke="red"
              strokeWidth={2}
              y={referenceLine.value}
              ifOverflow="extendDomain"
              label={{
                value: formatReferenceLineLabel(
                  referenceLine.value,
                  referenceLine.format,
                ),
                position: 'insideTopRight',
                fill: 'red',
              }}
            />
          )}
          {generateLineChartLineComponents({
            data,
            coloredLines,
            hideNodes,
            customChartNodeComponent,
          })}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
